import React, {useEffect, useState} from 'react';

import {fetch} from 'molbio/dao/FeatureLibraryDAO';
import {FeatureLibrary} from 'molbio/feature-libraries/FeatureLibraryConstants';
import {formatFeatureLibraryName} from 'molbio/feature-libraries/featureLibraryUtil';
import notifications from 'notifications';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {AsyncStatus, useAsyncStateOnMount} from 'ui-library/hooks/useAsyncState';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';

// This modal is _very_ similar to `AddAnnotationToFeatureLibraryModal` (this one is designed for
// multiple annotations while that one is for a single annotation). Let's consolidate.
export default function SaveAnnotationsAsFeaturesModal({
  annotationCount,
  onRequestSubmit,
  onRequestClose,
}: {
  annotationCount: number;
  onRequestSubmit: (featureLibraryId: number) => void;
  onRequestClose: () => void;
}): JSX.Element {
  const featureLibrariesState = useAsyncStateOnMount(fetch);
  const [selectedFeatureLibrary, setSelectedFeatureLibrary] = useState<FeatureLibrary>();
  useEffect(() => {
    if (featureLibrariesState.status === AsyncStatus.ERROR) {
      notifications.error('Encountered error loading feature libraries.');
      onRequestClose();
    } else if (featureLibrariesState.status === AsyncStatus.SUCCESS) {
      const writableFeatureLibraries = featureLibrariesState.data.filter(
        (library) => library.permissions.writable
      );
      if (writableFeatureLibraries.length > 0) {
        setSelectedFeatureLibrary(writableFeatureLibraries[0]);
      }
    }
  }, [featureLibrariesState, onRequestClose]);

  const modalProps = {
    title:
      annotationCount > 1
        ? `Save ${annotationCount} Annotations to Feature Library`
        : 'Save Annotation to Feature Library',
    onRequestClose,
    onRequestSubmit: () => {
      onRequestSubmit(selectedFeatureLibrary!.id);
    },
    buttons: [
      {text: 'Save Features', submit: true, disabled: selectedFeatureLibrary == null},
      {text: 'Cancel', cancel: true},
    ],
  };
  if (featureLibrariesState.status === AsyncStatus.SUCCESS) {
    const selectedFeatureLibraryDisplayName =
      selectedFeatureLibrary == null
        ? 'No feature library selected'
        : formatFeatureLibraryName(selectedFeatureLibrary);
    return (
      <Modal {...modalProps}>
        <SelectDropdown
          displayName={selectedFeatureLibraryDisplayName}
          menu={
            <SearchableMenu
              optionDatas={featureLibrariesState.data.map((library) => ({
                name: formatFeatureLibraryName(library),
                value: library,
                metadata: library.description,
                disabled: !library.permissions.writable,
                disabledMessage: 'You do not have sufficient permissions to write to this feature library.',
              }))}
              onSelect={setSelectedFeatureLibrary}
              selectedValue={selectedFeatureLibrary}
              width={MenuWidth.FULL}
            />
          }
        />
      </Modal>
    );
  } else {
    return (
      <Modal {...modalProps}>
        <Spinner />
      </Modal>
    );
  }
}
